import { Injectable } from '@angular/core';

/**
 * Well-known Local Storage keys used across the app. Callers may also pass a
 * caller-namespaced dynamic key (e.g. a server-provided per-hub × per-section
 * key) — the methods accept any `string`; this enum just registers the static,
 * app-owned keys so they stay collision-free and greppable.
 */
export enum LocalStorageKeys {
  COOKIES = 'cookies',
}

/**
 * A thin wrapper around the Local Storage API that **degrades safely** where
 * storage is unavailable — private-mode quotas, partitioned/sandboxed iframes,
 * or any context where touching `localStorage` throws. Support is probed once on
 * creation and every operation is additionally guarded, so callers never need
 * their own `try/catch`: writes no-op (including when a write exceeds quota) and
 * reads return `null` when storage can't be used.
 */
@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  /** Whether Local Storage is usable in this context (probed once on creation). */
  private readonly isLocalStorageSupported = this.queryLocalStorageSupport();

  /**
   * Store `value` under `key` when storage is available (a no-op otherwise, or
   * if the write itself throws). `key` is a {@link LocalStorageKeys} constant or
   * any caller-namespaced string; an empty `value` is stored as-is.
   */
  setItem(key: string, value: string): void {
    this.withStorage(() => localStorage.setItem(key, value), undefined);
  }

  /** Read the value stored under `key`, or `null` when absent / unavailable. */
  getItem(key: string): string | null {
    return this.withStorage(() => localStorage.getItem(key), null);
  }

  /** Remove `key` from storage when available (a no-op otherwise). */
  removeItem(key: string): void {
    this.withStorage(() => localStorage.removeItem(key), undefined);
  }

  /** Clear all keys from storage when available. */
  clear(): void {
    this.withStorage(() => localStorage.clear(), undefined);
  }

  /**
   * Run a Local Storage `operation`, returning `fallback` when storage is
   * unsupported or the operation throws. The support flag is a fast path for
   * known-bad contexts; the `try/catch` covers per-call failures the one-time
   * probe can't predict (quota exceeded on a real write, throw-on-access).
   */
  private withStorage<T>(operation: () => T, fallback: T): T {
    if (!this.isLocalStorageSupported) {
      return fallback;
    }
    try {
      return operation();
    } catch {
      return fallback;
    }
  }

  /**
   * Probe support with a write/remove round-trip — this throws (not just returns
   * null) under some private-mode quotas and sandboxed iframes, so the result is
   * cached and every other method gates on it.
   */
  private queryLocalStorageSupport(): boolean {
    try {
      const testKey = '__testKey';
      localStorage?.setItem(testKey, testKey);
      localStorage?.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }
}
